import { useEffect, useId, useRef } from "react";
import Draw, { DrawEvent, createBox } from "ol/interaction/Draw";
import { toLonLat } from "ol/proj";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import Style from "ol/style/Style";
import { ANNOTATION_COLOR } from "../constants/color";
import { useMap } from "../hooks";
import type { CustomPolygonStyleOverride } from "../layer/annotation/Polygon";

/**
 * WGS84 경계 상자 — **도(degree) 단위** `[경도, 위도]` 순서의 4-튜플.
 *
 * OL 의 `Extent` 와 형태(길이 4 숫자 배열)는 같지만 **좌표계가 다르다** — `Extent` 는 뷰 투영
 * 좌표계(이 앱은 Web Mercator 미터)다. 도 값을 투영 좌표를 받는 OL API(`View#fit` 등)에
 * 그대로 넘기면 경도 127·위도 37 이 미터로 해석돼 화면이 null island 로 점프한다.
 * 투영 좌표가 필요하면 래퍼가 재노출하는 `fromLonLat` 으로 변환한 뒤 넘긴다.
 */
export type LonLatExtent = [
  minLng: number,
  minLat: number,
  maxLng: number,
  maxLat: number
];

/**
 * headless 직사각형(박스) 드로잉 인터랙션 — **마운트=활성, 언마운트=완전 정리**.
 *
 * `PolygonDrawInteraction` 의 형제. OL 관용대로 `type: "Circle"` + `geometryFunction: createBox()`
 * 로 화면 축에 정렬된 직사각형을 그린다. 결과는 `onDrawEnd` 콜백으로만 흘러가며 **어떤 스토어에도
 * 쓰지 않는다** — 어노테이션이 아닌 휘발성 영역 선택(캡쳐 범위 지정 등)이 대상이기 때문이다.
 *
 * - 드래그(pointerdown → move → pointerup) = 한 번의 박스 완성, ESC/우클릭 = 취소(`onAbort`)
 * - 완성된 스케치는 콜백 후 자체 제거한다 — 결과 표시는 호출부가 소유한다
 * - 드래그 없는 클릭 등 `minSizeInPixels` 미만 입력은 선택으로 치지 않고 무시한다
 *
 * @example
 * <BoxDrawInteraction onDrawEnd={(lonLatExtent) => setCaptureExtent(lonLatExtent)} />
 */
export interface BoxDrawInteractionProps {
  /** 스케치 스타일 색상 (`ANNOTATION_COLOR` 키) */
  color?: keyof typeof ANNOTATION_COLOR;
  /**
   * 있으면 `color` 대신 이 선·채움으로 스케치를 그린다(선 폭 기본 2). 박스가 남길 결과에 주는 객체
   * (`CustomPolygon` 의 `styleOverride`)를 그대로 넘겨, 그리는 동안과 그린 뒤를 같은 색으로 보이려는 호출자용.
   * `lineDash` 는 그 위에 얹힌다. 마운트 때의 값으로 스타일을 만든다.
   */
  styleOverride?: CustomPolygonStyleOverride;
  /**
   * 박스 완성 시 WGS84 `[minLng, minLat, maxLng, maxLat]`(도) 전달 — 투영 좌표가 아니다.
   *
   * 화면 픽셀이 아니라 지리 좌표를 주는 이유: 선택 후 사용자가 지도를 이동·확대해도
   * 선택 영역이 따라가야 하므로, 픽셀은 필요한 시점에 호출부가 다시 환산해야 한다.
   */
  onDrawEnd: (lonLatExtent: LonLatExtent) => void;
  /** ESC/우클릭으로 스케치를 취소했을 때 — 호출부가 모드 종료 등 상태 전이를 수행 */
  onAbort?: () => void;
  /** 이보다 작은 박스는 무시한다(화면 픽셀 기준). @default 8 */
  minSizeInPixels?: number;
  /**
   * 스케치 테두리 점선 패턴(px, OL `lineDash`) — 「아직 그리는 중」을 점선으로 보이려는 호출자용.
   * 없으면 실선. 마운트 때의 값으로 스타일을 만든다.
   */
  lineDash?: number[];
}

const SKETCH_STYLE_Z_INDEX = 1000;
const SKETCH_STROKE_WIDTH = 2;
const SKETCH_FILL_OPACITY = 0.1;
const DEFAULT_MIN_SIZE_IN_PIXELS = 8;
/**
 * 그리기 상태(`isDrawing`·커서)의 현재 소유자를 표시하는 map property.
 *
 * 툴바에서 도구를 바꾸면 새 도구의 클릭 핸들러가 먼저 `isDrawing`/커서를 세우고, 그 다음에야
 * 이 컴포넌트의 언마운트 cleanup 이 돈다. 소유권 확인 없이 되돌리면 **방금 켠 도구의 상태를
 * 꺼뜨린다** — 작도 중인데 isDrawing 이 false 라 클릭이 어노테이션 선택으로 새어 나간다.
 */
const DRAWING_OWNER = "drawingOwner";

/** 스케치 스타일 — `override` 가 있으면 그 선·채움, 없으면 팔레트 색. 점선은 어느 쪽에도 얹힌다 */
function createSketchStyle(
  color: keyof typeof ANNOTATION_COLOR,
  override: CustomPolygonStyleOverride | undefined,
  lineDash: number[] | undefined
): Style {
  return new Style({
    zIndex: SKETCH_STYLE_Z_INDEX,
    stroke: new Stroke({
      color: override ? override.strokeColor : ANNOTATION_COLOR[color].stroke(1),
      width: override?.strokeWidth ?? SKETCH_STROKE_WIDTH,
      lineDash,
    }),
    fill: new Fill({
      color: override
        ? override.fillColor
        : ANNOTATION_COLOR[color].fill(SKETCH_FILL_OPACITY),
    }),
  });
}

export function BoxDrawInteraction({
  color = "BLUE",
  onDrawEnd,
  onAbort,
  minSizeInPixels = DEFAULT_MIN_SIZE_IN_PIXELS,
  lineDash,
  styleOverride,
}: BoxDrawInteractionProps) {
  const map = useMap();
  // 콜백 최신값 ref — 콜백 identity 변화로 인터랙션이 재설치되지 않게 한다.
  // 렌더 중 ref 쓰기는 React Compiler 최적화를 막으므로 커밋 후(effect) 동기화한다.
  const onDrawEndRef = useRef(onDrawEnd);
  const onAbortRef = useRef(onAbort);
  const minSizeRef = useRef(minSizeInPixels);
  // 스케치 스타일은 설치 때 한 번 만든다 — 배열·객체 prop 의 identity 로 인터랙션이 재설치되지 않게 ref 로 읽는다
  const lineDashRef = useRef(lineDash);
  const styleOverrideRef = useRef(styleOverride);
  useEffect(() => {
    onDrawEndRef.current = onDrawEnd;
    onAbortRef.current = onAbort;
    minSizeRef.current = minSizeInPixels;
    lineDashRef.current = lineDash;
    styleOverrideRef.current = styleOverride;
  });

  const ownerId = useId();

  useEffect(() => {
    const sketchSource = new VectorSource();
    const sketchLayer = new VectorLayer({ source: sketchSource });
    map.addLayer(sketchLayer);

    const draw = new Draw({
      type: "Circle",
      geometryFunction: createBox(),
      // 드래그 한 번으로 완성 — 스펙의 "한 지점부터 다른 지점까지 드래그" 제스처.
      freehand: true,
      source: sketchSource,
      style: createSketchStyle(
        color,
        styleOverrideRef.current,
        lineDashRef.current
      ),
    });

    const handleDrawEnd = (event: DrawEvent) => {
      const geometry = event.feature.getGeometry();
      // OL 은 drawend 디스패치 직후 완성 피처를 source 에 넣는다 —
      // 마이크로태스크로 비워 결과 표시 소유권을 호출부에 넘긴다(잔여 스케치 방지)
      queueMicrotask(() => sketchSource.clear());
      if (!geometry) return;

      const [minX, minY, maxX, maxY] = geometry.getExtent();
      const topLeft = map.getPixelFromCoordinate([minX, maxY]);
      const bottomRight = map.getPixelFromCoordinate([maxX, minY]);
      if (!topLeft || !bottomRight) return;

      const widthInPixels = Math.abs(bottomRight[0] - topLeft[0]);
      const heightInPixels = Math.abs(bottomRight[1] - topLeft[1]);
      if (
        widthInPixels < minSizeRef.current ||
        heightInPixels < minSizeRef.current
      ) {
        return;
      }

      const [minLng, minLat] = toLonLat([minX, minY]);
      const [maxLng, maxLat] = toLonLat([maxX, maxY]);
      onDrawEndRef.current([minLng, minLat, maxLng, maxLat]);
    };
    draw.on("drawend", handleDrawEnd);
    map.addInteraction(draw);
    map.setProperties({ isDrawing: true, [DRAWING_OWNER]: ownerId });

    const viewport = map.getViewport();
    const previousCursor = viewport.style.cursor;
    viewport.style.cursor = "crosshair";

    const abortSketch = () => {
      draw.abortDrawing();
      sketchSource.clear();
      onAbortRef.current?.();
    };
    const handleContextMenu = (event: MouseEvent) => {
      event.preventDefault();
      abortSketch();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") abortSketch();
    };
    viewport.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      viewport.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
      draw.un("drawend", handleDrawEnd);
      map.removeInteraction(draw);
      map.removeLayer(sketchLayer);
      sketchSource.clear();
      // 그 사이 다른 도구가 그리기를 시작했으면 그쪽 상태를 건드리지 않는다.
      if (map.get(DRAWING_OWNER) === ownerId) {
        map.setProperties({ isDrawing: false, [DRAWING_OWNER]: undefined });
        viewport.style.cursor = previousCursor;
      }
    };
  }, [map, color, ownerId]);

  return null;
}
